import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/layout/CookieConsent";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { routeTranslations, Language, getLocalizedPath } from "@/utils/localizedRoutes";

// Critical pages - loaded immediately
import HomePage from "./pages/HomePage";
import NotFound from "./pages/NotFound";

// Lazy load public pages (code-splitting)
const CalculatorPage = lazy(() => import("./pages/CalculatorPage"));
const CennikPage = lazy(() => import("./pages/CennikPage"));
const ONasPage = lazy(() => import("./pages/ONasPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const KontaktPage = lazy(() => import("./pages/KontaktPage"));
const OchranaSukromiaPage = lazy(() => import("./pages/OchranaSukromiaPage"));
const ObchodnePodmienkyPage = lazy(() => import("./pages/ObchodnePodmienkyPage"));
const TranslatorResponsePage = lazy(() => import("./pages/TranslatorResponsePage"));

// Lazy load service pages
const SluzbyPage = lazy(() => import("./pages/sluzby/SluzbyPage"));
const UradnePrekladyPage = lazy(() => import("./pages/sluzby/UradnePrekladyPage"));
const LegalizaciaDopytPage = lazy(() => import("./pages/sluzby/LegalizaciaDopytPage"));
const ApostilaPage = lazy(() => import("./pages/sluzby/ApostilaPage"));
const ApostilaDocumentDetailPage = lazy(() => import("./pages/sluzby/ApostilaDocumentDetailPage"));
const SuperlegalizaciaPage = lazy(() => import("./pages/sluzby/SuperlegalizaciaPage"));
const ZahranicneDokumentyPage = lazy(() => import("./pages/sluzby/ZahranicneDokumentyPage"));
const ZahranicneDokumentyDetailPage = lazy(() => import("./pages/sluzby/ZahranicneDokumentyDetailPage"));

// Lazy load blog pages
const BlogListPage = lazy(() => import("./pages/blog/BlogListPage"));
const BlogDetailPage = lazy(() => import("./pages/blog/BlogDetailPage"));

// Lazy load CRM pages (separate chunk)
const LoginPage = lazy(() => import("./pages/crm/LoginPage"));
const DashboardPage = lazy(() => import("./pages/crm/DashboardPage"));
const OrdersPage = lazy(() => import("./pages/crm/OrdersPage"));
const OrderDetailPage = lazy(() => import("./pages/crm/OrderDetailPage"));
const NewOrderPage = lazy(() => import("./pages/crm/NewOrderPage"));
const ClientsPage = lazy(() => import("./pages/crm/ClientsPage"));
const ClientDetailPage = lazy(() => import("./pages/crm/ClientDetailPage"));
const NewClientPage = lazy(() => import("./pages/crm/NewClientPage"));
const SettingsPage = lazy(() => import("./pages/crm/SettingsPage"));
const CalculatorSessionsPage = lazy(() => import("./pages/crm/CalculatorSessionsPage"));
const LegalizationInquiriesPage = lazy(() => import("./pages/crm/LegalizationInquiriesPage"));
const ContactInquiriesPage = lazy(() => import("./pages/crm/ContactInquiriesPage"));
const BlogPostsPage = lazy(() => import("./pages/crm/BlogPostsPage"));
const BlogPostEditPage = lazy(() => import("./pages/crm/BlogPostEditPage"));

// Skeleton components for loading states
import { PageSkeleton, DocumentDetailSkeleton, ListPageSkeleton } from "@/components/skeletons/PageSkeleton";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Language codes for routing
const LANG_CODES: Language[] = ['en', 'de', 'uk', 'ru', 'es'];

// Map Slovak paths to component factories (lazy evaluation inside provider)
const routeComponents: Record<string, () => React.ReactElement> = {
  '/': () => <HomePage />,
  '/kalkulator': () => <Suspense fallback={<PageSkeleton />}><CalculatorPage /></Suspense>,
  '/cennik': () => <Suspense fallback={<PageSkeleton />}><CennikPage /></Suspense>,
  '/o-nas': () => <Suspense fallback={<PageSkeleton />}><ONasPage /></Suspense>,
  '/faq': () => <Suspense fallback={<PageSkeleton />}><FAQPage /></Suspense>,
  '/kontakt': () => <Suspense fallback={<PageSkeleton />}><KontaktPage /></Suspense>,
  '/ochrana-sukromia': () => <Suspense fallback={<PageSkeleton />}><OchranaSukromiaPage /></Suspense>,
  '/obchodne-podmienky': () => <Suspense fallback={<PageSkeleton />}><ObchodnePodmienkyPage /></Suspense>,
  '/sluzby': () => <Suspense fallback={<PageSkeleton />}><SluzbyPage /></Suspense>,
  '/sluzby/uradne-preklady': () => <Suspense fallback={<DocumentDetailSkeleton />}><UradnePrekladyPage /></Suspense>,
  '/sluzby/dopyt': () => <Suspense fallback={<PageSkeleton />}><LegalizaciaDopytPage /></Suspense>,
  '/sluzby/apostila': () => <Suspense fallback={<ListPageSkeleton />}><ApostilaPage /></Suspense>,
  '/sluzby/superlegalizacia': () => <Suspense fallback={<ListPageSkeleton />}><SuperlegalizaciaPage /></Suspense>,
  '/sluzby/zahranicne-dokumenty': () => <Suspense fallback={<ListPageSkeleton />}><ZahranicneDokumentyPage /></Suspense>,
};

function LegacyForeignDocsRedirect({ lang }: { lang: Language }) {
  const { slug } = useParams<{ slug: string }>();
  const to = getLocalizedPath(`/sluzby/zahranicne-dokumenty/${slug ?? ''}`, lang);
  return <Navigate to={to} replace />;
}

// Helper to generate localized routes
function LocalizedRoutes() {
  const routes: React.ReactElement[] = [];

  // Generate routes for each Slovak path
  Object.entries(routeComponents).forEach(([skPath, createComponent]) => {
    const translations = routeTranslations[skPath];
    
    if (!translations) {
      routes.push(<Route key={skPath} path={skPath} element={createComponent()} />);
      return;
    }

    // Slovak route (no prefix)
    routes.push(
      <Route key={`sk-${skPath}`} path={translations.sk} element={createComponent()} />
    );

    // Localized routes with language prefix
    LANG_CODES.forEach((lang) => {
      const localizedPath = translations[lang];
      const fullPath = localizedPath === '/' ? `/${lang}` : `/${lang}${localizedPath}`;
      routes.push(
        <Route key={`${lang}-${skPath}`} path={fullPath} element={createComponent()} />
      );
    });
  });

  return (
    <Routes>
      {routes}

      {/* Legacy (Slovak) foreign-documents paths under language prefixes */}
      <Route
        path="/en/sluzby/zahranicne-dokumenty"
        element={<Navigate to="/en/services/foreign-documents" replace />}
      />
      <Route
        path="/en/sluzby/zahranicne-dokumenty/:slug"
        element={<LegacyForeignDocsRedirect lang="en" />}
      />

      <Route
        path="/de/sluzby/zahranicne-dokumenty"
        element={<Navigate to="/de/dienstleistungen/auslaendische-dokumente" replace />}
      />
      <Route
        path="/de/sluzby/zahranicne-dokumenty/:slug"
        element={<LegacyForeignDocsRedirect lang="de" />}
      />

      <Route
        path="/uk/sluzby/zahranicne-dokumenty"
        element={<Navigate to="/uk/posluhy/inozemni-dokumenty" replace />}
      />
      <Route
        path="/uk/sluzby/zahranicne-dokumenty/:slug"
        element={<LegacyForeignDocsRedirect lang="uk" />}
      />

      <Route
        path="/ru/sluzby/zahranicne-dokumenty"
        element={<Navigate to="/ru/uslugi/inostrannye-dokumenty" replace />}
      />
      <Route
        path="/ru/sluzby/zahranicne-dokumenty/:slug"
        element={<LegacyForeignDocsRedirect lang="ru" />}
      />

      <Route
        path="/es/sluzby/zahranicne-dokumenty"
        element={<Navigate to="/es/servicios/documentos-extranjeros" replace />}
      />
      <Route
        path="/es/sluzby/zahranicne-dokumenty/:slug"
        element={<LegacyForeignDocsRedirect lang="es" />}
      />

      {/* Dynamic country detail pages for foreign documents */}
      <Route
        path="/sluzby/zahranicne-dokumenty/:slug"
        element={
          <Suspense fallback={<DocumentDetailSkeleton />}>
            <ZahranicneDokumentyDetailPage />
          </Suspense>
        }
      />
      <Route path="/en/services/foreign-documents/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ZahranicneDokumentyDetailPage /></Suspense>} />
      <Route path="/de/dienstleistungen/auslaendische-dokumente/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ZahranicneDokumentyDetailPage /></Suspense>} />
      <Route path="/uk/posluhy/inozemni-dokumenty/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ZahranicneDokumentyDetailPage /></Suspense>} />
      <Route path="/ru/uslugi/inostrannye-dokumenty/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ZahranicneDokumentyDetailPage /></Suspense>} />
      <Route path="/es/servicios/documentos-extranjeros/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ZahranicneDokumentyDetailPage /></Suspense>} />

      {/* Dynamic document type detail pages for apostila */}
      <Route path="/sluzby/apostila/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ApostilaDocumentDetailPage /></Suspense>} />
      <Route path="/en/services/apostille/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ApostilaDocumentDetailPage /></Suspense>} />
      <Route path="/de/dienstleistungen/apostille/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ApostilaDocumentDetailPage /></Suspense>} />
      <Route path="/uk/posluhy/apostyl/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ApostilaDocumentDetailPage /></Suspense>} />
      <Route path="/ru/uslugi/apostil/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ApostilaDocumentDetailPage /></Suspense>} />
      <Route path="/es/servicios/apostilla/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><ApostilaDocumentDetailPage /></Suspense>} />
      
      {/* Blog routes */}
      <Route path="/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogListPage /></Suspense>} />
      <Route path="/blog/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogDetailPage /></Suspense>} />
      <Route path="/en/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogListPage /></Suspense>} />
      <Route path="/en/blog/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogDetailPage /></Suspense>} />
      <Route path="/de/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogListPage /></Suspense>} />
      <Route path="/de/blog/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogDetailPage /></Suspense>} />
      <Route path="/uk/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogListPage /></Suspense>} />
      <Route path="/uk/blog/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogDetailPage /></Suspense>} />
      <Route path="/ru/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogListPage /></Suspense>} />
      <Route path="/ru/blog/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogDetailPage /></Suspense>} />
      <Route path="/es/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogListPage /></Suspense>} />
      <Route path="/es/blog/:slug" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogDetailPage /></Suspense>} />

      {/* Public utility routes */}
      <Route path="/translator-response" element={<Suspense fallback={<PageSkeleton />}><TranslatorResponsePage /></Suspense>} />

      {/* CRM Routes */}
      <Route path="/crm/login" element={<Suspense fallback={<PageSkeleton />}><LoginPage /></Suspense>} />
      <Route path="/crm" element={<Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>} />
      <Route path="/crm/objednavky" element={<Suspense fallback={<ListPageSkeleton />}><OrdersPage /></Suspense>} />
      <Route path="/crm/objednavky/nova" element={<Suspense fallback={<PageSkeleton />}><NewOrderPage /></Suspense>} />
      <Route path="/crm/objednavky/:id" element={<Suspense fallback={<DocumentDetailSkeleton />}><OrderDetailPage /></Suspense>} />
      <Route path="/crm/klienti" element={<Suspense fallback={<ListPageSkeleton />}><ClientsPage /></Suspense>} />
      <Route path="/crm/klienti/novy" element={<Suspense fallback={<PageSkeleton />}><NewClientPage /></Suspense>} />
      <Route path="/crm/klienti/:id" element={<Suspense fallback={<DocumentDetailSkeleton />}><ClientDetailPage /></Suspense>} />
      <Route path="/crm/nastavenia" element={<Suspense fallback={<PageSkeleton />}><SettingsPage /></Suspense>} />
      <Route path="/crm/kalkulator" element={<Suspense fallback={<ListPageSkeleton />}><CalculatorSessionsPage /></Suspense>} />
      <Route path="/crm/legalizacia-dopyty" element={<Suspense fallback={<ListPageSkeleton />}><LegalizationInquiriesPage /></Suspense>} />
      <Route path="/crm/kontakt-spravy" element={<Suspense fallback={<ListPageSkeleton />}><ContactInquiriesPage /></Suspense>} />
      <Route path="/crm/blog" element={<Suspense fallback={<ListPageSkeleton />}><BlogPostsPage /></Suspense>} />
      <Route path="/crm/blog/:id" element={<Suspense fallback={<DocumentDetailSkeleton />}><BlogPostEditPage /></Suspense>} />
      
      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageProvider>
          <ScrollToTop />
          <LocalizedRoutes />
          <CookieConsent />
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
